import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { useHistory } from "react-router-dom";
import { PlxsIcon, PlxsChip } from "@plexlogic/plexus-ui";
import randomDrawsLogo from "../assets/random-draw-wizard-neutral.png";
import {
  getStoredDrawIds,
  setWizardSession,
  resetWizardForNewDraw,
} from "../services/wizardStorage";

const AppHeader: React.FC = () => {
  const { user, isLoading, loginWithRedirect, logout } = useAuth();
  const { isSuperUser } = useRBAC();
  const history = useHistory();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Handle clicking outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Handle navigation with proper wizard reset
  const handleNavigation = (path: string) => {
    if (path === "/draws/wizard") {
      resetWizardForNewDraw();
      window.location.href = path;
    } else {
      history.push(path);
    }
    setIsDropdownOpen(false);
  };

  const homePath = user ? "/dashboard" : "/draws/wizard";

  // Base navigation links
  const baseNavigationLinks = [
    { path: "/dashboard", label: "Dashboard", icon: "grid" as const },
    { path: "/draws/wizard", label: "New draw", icon: "add" as const },
    { path: "/audit", label: "My activity", icon: "file" as const },
  ];

  // Super User only links
  const superUserLinks = [
    { path: "/monitoring", label: "Monitoring", icon: "checkInCircle" as const, superUserOnly: true },
  ];

  // Combine links based on user role
  const navigationLinks = isSuperUser 
    ? [...baseNavigationLinks, ...superUserLinks]
    : baseNavigationLinks;

  // Get organization from user's Legal Gateway data
  const organizationName = user?.rootOrganisationName || "";

  if (isLoading) {
    return (
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "var(--plexus-color-header-bg)",
          color: "var(--plexus-color-header-text)",
          padding: "var(--plexus-space-md, 1rem)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid var(--plexus-color-neutral-500)",
          minHeight: "64px",
        }}
      >
        <div
          style={{
            width: "24px",
            height: "24px",
            border: "2px solid var(--plexus-color-neutral-300)",
            borderTop: "2px solid var(--plexus-color-neutral-800)",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </header>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .app-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background-color: var(--plexus-color-header-bg);
          color: var(--plexus-color-header-text);
          padding: 4px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid var(--plexus-color-neutral-500);
          min-height: 64px;
          gap: 12px;
        }
        
        .header-left {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1 1 auto;
          min-width: 0;
        }
        
        .header-logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }
        
        .header-logo-container:hover {
          opacity: 0.8;
        }
        
        .header-logo-svg {
          height: 56px;
          width: auto;
          max-width: 400px;
          object-fit: contain;
        }
        
        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
          height: 100%;
        }
        
        .org-dropdown {
          position: relative;
          display: flex;
          align-items: center;
          background-color: var(--plexus-color-brand-400);
          border-radius: 20px;
          padding: 8px 14px;
          cursor: default;
          transition: all 0.2s ease;
          border: none;
          min-width: 100px;
          height: auto;
        }
        
        .org-dropdown:hover {
          background-color: var(--plexus-color-brand-400);
        }
        
        .org-name {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--plexus-color-surface-50);
          white-space: nowrap;
          flex: 1;
        }
        
        .org-arrow {
          margin-left: 4px;
          width: 0;
          height: 0;
          border-left: 2px solid transparent;
          border-right: 2px solid transparent;
          border-top: 3px solid var(--plexus-color-neutral-600);
          flex-shrink: 0;
        }
        
        .org-select {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }
        
        .user-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px;
          background-color: var(--plexus-color-surface-100);
          border-radius: 16px;
          border: 1px solid var(--plexus-color-neutral-500);
        }
        
        .user-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--plexus-color-neutral-500);
          flex-shrink: 0;
        }
        
        .user-avatar-placeholder {
          width: 24px;
          height: 24px;
          background-color: var(--plexus-color-surface-200);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          border: 2px solid var(--plexus-color-neutral-500);
          flex-shrink: 0;
        }
        
        .user-email {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--plexus-color-header-text);
          white-space: nowrap;
        }
        
        /* New User Menu Styles */
        .user-menu {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .user-avatar-button {
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 50%;
          padding: 2px;
          background-color: var(--plexus-color-neutral-600);
        }
        
        .user-avatar-button:hover {
          transform: scale(1.05);
          background-color: var(--plexus-color-brand-600);
        }
        
        .user-avatar-main {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--plexus-color-surface-50);
          transition: all 0.2s ease;
          cursor: pointer;
          touch-action: manipulation;
        }
        
        .user-avatar-main-placeholder {
          width: 32px;
          height: 32px;
          background-color: var(--plexus-color-surface-50);
          color: var(--plexus-color-brand-400);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          font-weight: 700;
          border: none;
          transition: all 0.2s ease;
          cursor: pointer;
          touch-action: manipulation;
        }
        
        .user-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 4px;
          background-color: var(--color-surface, var(--plexus-color-surface-50));
          color: var(--color-text, var(--plexus-color-neutral-100));
          border-radius: 12px;
          box-shadow: 0 8px 32px var(--plexus-color-neutral-400);
          border: 1px solid var(--plexus-color-neutral-500);
          min-width: 280px;
          max-width: 420px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-8px);
          transition: all 0.2s ease;
          z-index: 1001;
          overflow: hidden;
          white-space: nowrap;
          pointer-events: none;
        }
        
        /* Create a hover bridge to prevent dropdown from closing */
        .user-menu::after {
          content: '';
          position: absolute;
          top: 100%;
          right: 0;
          width: 100%;
          height: 8px;
          background: transparent;
          z-index: 1000;
        }
        
        .user-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          pointer-events: auto;
        }
        
        /* Removed hover behavior - now controlled by click only */
        
        .user-dropdown-header {
          padding: 16px;
          border-bottom: 1px solid var(--plexus-color-neutral-600);
          background-color: var(--plexus-color-surface-100);
        }
        
        .user-dropdown-email {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text, var(--plexus-color-neutral-100));
          line-height: 1.4;
          word-break: break-all;
          max-width: none;
          overflow-wrap: break-word;
          hyphens: auto;
          padding-right: 8px;
          white-space: normal;
        }
        
        .user-dropdown-divider {
          height: 1px;
          background-color: var(--plexus-color-neutral-600);
          margin: 0;
        }
        
        .user-dropdown-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: none;
          border: none;
          color: var(--color-text, var(--plexus-color-neutral-100));
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
          text-align: left;
        }
        
        /* White, not --color-text-inverse: that alias resolves to dark header text in
           the neutral theme. Icons target svg because PlxsIcon colours its own svg
           and never emits a plxs-icon class. */
        .user-dropdown-item:hover {
          background-color: var(--plexus-color-brand-400); /* TODO: Updated to Plexus token */
          color: var(--plexus-color-surface-50);
        }

        .user-dropdown-item:hover svg {
          color: var(--plexus-color-surface-50);
        }

        .user-dropdown-item:focus {
          outline: none;
          background-color: var(--plexus-color-brand-400); /* TODO: Updated to Plexus token */
          color: var(--plexus-color-surface-50);
        }

        .user-dropdown-item:focus svg {
          color: var(--plexus-color-surface-50);
        }
        
        .user-dropdown-item .plxs-icon {
          margin-right: var(--plexus-space-sm);
          font-size: 1rem;
        }
        
        .logout-button {
          background-color: var(--plexus-color-surface-100);
          color: var(--plexus-color-header-text);
          border: 1px solid var(--plexus-color-neutral-500);
          border-radius: 16px;
          padding: 8px 16px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          box-shadow: 0 2px 4px var(--plexus-color-neutral-400);
        }
        
        .logout-button:hover {
          background-color: var(--plexus-color-surface-200);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px var(--plexus-color-neutral-400);
        }
        
        .logout-button:focus {
          outline: none;
          box-shadow: 0 0 0 2px var(--plexus-color-neutral-500);
        }
        
        .login-button {
          background-color: var(--plexus-color-brand-400);
          color: var(--plexus-color-surface-50);
          border: none;
          border-radius: 20px;
          padding: 10px 20px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        
        .login-button:hover {
          background-color: var(--plexus-color-brand-300);
          transform: translateY(-1px);
        }
        
        .login-button:focus {
          outline: none;
          box-shadow: 0 0 0 2px var(--plexus-color-brand-500);
        }
        
        /* Enhanced responsive design for better mobile experience */
        
        @media (max-width: 1024px) {
          .app-header {
            padding: 8px 18px;
            gap: 10px;
          }
          
          .header-title {
            font-size: 0.9rem;
          }
          
          .org-dropdown {
            min-width: 85px;
          }
          
          .user-dropdown {
            min-width: 250px;
            max-width: 350px;
          }
        }
        
        @media (max-width: 768px) {
          .app-header {
            padding: 6px 14px;
            gap: 8px;
            min-height: 44px;
            flex-wrap: nowrap;
          }
          
          .header-left {
            gap: 6px;
            flex: 1;
            min-width: 0;
          }
          
          .header-logo-svg {
            height: 48px;
            max-width: 280px;
          }
          
          .header-right {
            gap: 6px;
            flex-shrink: 0;
          }
          
          .org-dropdown {
            min-width: 75px;
            padding: 2px 5px;
          }
          
          .org-name {
            font-size: 0.7rem;
          }
          
          .user-avatar-main,
          .user-avatar-main-placeholder {
            width: 26px;
            height: 26px;
          }
          
          .user-dropdown {
            min-width: 230px;
            max-width: 290px;
            right: 0;
          }
          
          .logout-button {
            padding: 5px 10px;
            font-size: 0.75rem;
          }
        }
        
        @media (max-width: 640px) {
          .app-header {
            padding: 5px 10px;
            gap: 6px;
            min-height: 40px;
          }
          
          .header-left {
            gap: 5px;
            min-width: 0;
            flex: 1;
          }
          
          .header-logo-svg {
            height: 44px;
            max-width: 240px;
          }
          
          .header-right {
            gap: 5px;
            flex-shrink: 0;
          }
          
          .org-dropdown {
            min-width: 65px;
            padding: 1px 4px;
          }
          
          .org-name {
            font-size: 0.65rem;
          }
          
          .user-avatar-main,
          .user-avatar-main-placeholder {
            width: 22px;
            height: 22px;
          }
          
          .user-dropdown {
            min-width: 210px;
            max-width: 270px;
            right: -10px;
          }
          
          .user-dropdown-email {
            font-size: 0.8rem;
            padding-right: 6px;
          }
          
          .logout-button {
            padding: 4px 8px;
            font-size: 0.75rem;
          }
        }
        
        @media (max-width: 480px) {
          .app-header {
            padding: 4px 8px;
            gap: 4px;
            min-height: 36px;
          }
          
          .header-left {
            gap: 4px;
          }
          
          .header-logo-svg {
            height: 40px;
            max-width: 200px;
          }
          
          .header-right {
            gap: 4px;
          }
          
          .org-dropdown {
            min-width: 55px;
            padding: 1px 3px;
          }
          
          .org-name {
            font-size: 0.6rem;
          }
          
          .user-avatar-main,
          .user-avatar-main-placeholder {
            width: 20px;
            height: 20px;
          }
          
          .user-dropdown {
            min-width: 190px;
            max-width: 250px;
            right: -12px;
          }
          
          .user-dropdown-header {
            padding: 10px;
          }
          
          .user-dropdown-email {
            font-size: 0.7rem;
          }
          
          .logout-button {
            padding: 2px 5px;
            font-size: 0.65rem;
          }
        }
      `}</style>

      <header className="app-header">
        <div className="header-left">
          <div 
            className="header-logo-container"
            onClick={() => handleNavigation(homePath)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNavigation(homePath);
              }
            }}
          >
            <img
              src={randomDrawsLogo}
              alt="Plexus Random Draw"
              className="header-logo-svg"
            />
          </div>
        </div>

        <div className="header-right">
          {/* User Section */}
          {user ? (
            <>
              {/* Organization Display - Only visible when authenticated and organization exists */}
              {organizationName && (
                <PlxsChip
                  decorativeIcon="home"
                  label={organizationName}
                  tone="brand"
                />
              )}

              {/* User Avatar with Menu */}
              <div className="user-menu" ref={dropdownRef}>
                <div 
                  className="user-avatar-button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {user.picture ? (
                    <img
                      className="user-avatar-main"
                      src={user.picture}
                      alt="User"
                    />
                  ) : (
                    <div className="user-avatar-main-placeholder">
                      {user.email?.charAt(0).toUpperCase() || "U"}
                    </div>
                  )}
                </div>

                <div className={`user-dropdown ${isDropdownOpen ? 'open' : ''}`}>
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-email">
                      {user.email || user.name || "User"}
                    </div>
                  </div>

                  <div className="user-dropdown-divider"></div>

                  {/* Navigation Links */}
                  {navigationLinks.map((link) => (
                    <button
                      key={link.path}
                      className="user-dropdown-item"
                      onClick={() => handleNavigation(link.path)}
                    >
                      <PlxsIcon name={link.icon} />
                      {link.label}
                    </button>
                  ))}

                  <div className="user-dropdown-divider"></div>

                  <button
                    className="user-dropdown-item"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      logout();
                    }}
                  >
                    <PlxsIcon name="logout" />
                    Logout
                  </button>
                </div>
              </div>
            </>
          ) : (
            <button
              className="login-button"
              onClick={() => {
                const [latestDrawId] = getStoredDrawIds();
                if (latestDrawId) {
                  setWizardSession({ drawId: latestDrawId }, 0);
                  sessionStorage.setItem("isRedirectingToLogin", "true");
                }
                loginWithRedirect();
              }}
            >
              Login
            </button>
          )}
        </div>
      </header>
    </>
  );
};

export default AppHeader;
